import { type FormEvent, useState } from 'react';
import { PageHead } from 'ui/kit/PageHead';
import { Link, useNavigate } from 'react-router';

import { WalletRubAmountInput } from 'components/Wallet/WalletRubAmountInput';
import { validateWalletAmountRub } from 'components/Wallet/walletAmountLimits';
import { WalletTransactionHistory } from 'components/Wallet/WalletTransactionHistory';
import {
  useTutorWallet,
  useWalletRefundRequestCreate,
  useWalletRefundRequests,
  useWalletTopUp,
} from 'hooks/useTutorWallet';
import { formatRubFull, parseRubAmount } from 'utils/formatMoneyRu';

import classes from './t3Screen.module.scss';

function formatDate(value?: string | null): string {
  if (!value) return ' - ';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return ' - ';
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export const TeacherWallet = () => {
  const navigate = useNavigate();
  const { data: wallet, isLoading, error } = useTutorWallet();
  const { data: refundRequests } = useWalletRefundRequests();
  const topUp = useWalletTopUp();
  const refundCreate = useWalletRefundRequestCreate();

  const [topUpAmount, setTopUpAmount] = useState<number | ''>(1000);
  const [showTopUp, setShowTopUp] = useState(false);
  const [showRefund, setShowRefund] = useState(false);
  const [refundAmount, setRefundAmount] = useState<number | ''>('');
  const [refundReason, setRefundReason] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const handleTopUp = async (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);
    const amount = topUpAmount === '' ? NaN : topUpAmount;
    const amountError = validateWalletAmountRub(amount);
    if (amountError) {
      setFormError(amountError);
      return;
    }
    try {
      const created = await topUp.mutateAsync(amount);
      navigate(created.checkout_url);
    } catch (err: unknown) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
        'Не удалось создать пополнение';
      setFormError(typeof detail === 'string' ? detail : 'Не удалось создать пополнение');
    }
  };

  const handleRefund = async (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);
    const amount = refundAmount === '' ? NaN : refundAmount;
    const amountError = validateWalletAmountRub(amount);
    if (amountError) {
      setFormError(amountError);
      return;
    }
    try {
      await refundCreate.mutateAsync({
        amount_rub: amount,
        reason: refundReason.trim() || undefined,
      });
      setShowRefund(false);
      setRefundAmount('');
      setRefundReason('');
    } catch (err: unknown) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
        'Не удалось отправить заявку';
      setFormError(typeof detail === 'string' ? detail : 'Не удалось отправить заявку');
    }
  };

  if (isLoading) {
    return (
      <main className={classes.page}>
        <PageHead crumb={<>Личный кабинет / Кошелек</>} title={<>Кошелек</>} />
        <p className={classes.body}>Загрузка баланса...</p>
      </main>
    );
  }

  if (error) {
    return (
      <main className={classes.page}>
        <PageHead crumb={<>Личный кабинет / Кошелек</>} title={<>Кошелек</>} />
        <p className={classes.body}>Не удалось загрузить баланс. Попробуйте обновить страницу.</p>
      </main>
    );
  }

  return (
    <main className={classes.page}>
      <PageHead crumb={<>Личный кабинет / Кошелек</>} title={<>Кошелек</>} />
      <section className={classes.card}>
        <h2 className={classes.cardTitle}>Баланс</h2>
        <p className={classes.muted}>
          Раздел виден только при включенной функции кошелька. Оплату урока вы согласуете с учеником
          отдельно.
        </p>
        {wallet?.provider === 'test' ? <p className={classes.small}>Тестовый режим</p> : null}
        <p className={classes.amount}>{formatRubFull(wallet?.available_rub ?? '0.00')}</p>
        <div className={classes.line}>
          <strong>Зарезервировано</strong>
          <span>{formatRubFull(wallet?.reserved_rub ?? '0.00')}</span>
        </div>
        {wallet?.pending_refund_rub && wallet.pending_refund_rub !== '0.00' ? (
          <p className={classes.small}>В обработке возврата: {formatRubFull(wallet.pending_refund_rub)}</p>
        ) : null}
        <div className={classes.callout}>
          <strong>Для операций сервиса</strong>
          <span>
            Оплату урока ученик и репетитор согласуют между собой. Баланс кошелька не подтверждает
            оплату занятия.
          </span>
        </div>
        <button
          type='button'
          className={classes.primary}
          onClick={() => {
            setFormError(null);
            setShowTopUp((value) => !value);
            setShowRefund(false);
          }}
        >
          Пополнить кошелек
        </button>
        <button
          type='button'
          className={classes.secondary}
          onClick={() => {
            setFormError(null);
            setShowRefund((value) => !value);
            setShowTopUp(false);
            const available = parseRubAmount(wallet?.available_rub ?? '0');
            setRefundAmount(available >= 100 ? available : '');
          }}
        >
          Запросить возврат
        </button>
      </section>

      {showTopUp ? (
        <form className={classes.card} onSubmit={handleTopUp}>
          <h2 className={classes.cardTitle}>Пополнение</h2>
          <p className={classes.muted}>
            Это демо-заказ сервиса. Деньги не списываются. Номер карты на наш сервер не
            отправляется. Стоимость занятий вы согласуете с учеником отдельно.
          </p>
          <WalletRubAmountInput
            value={topUpAmount}
            onChange={setTopUpAmount}
            disabled={topUp.isPending}
          />
          {formError ? <p className={classes.error}>{formError}</p> : null}
          <button type='submit' className={classes.primary} disabled={topUp.isPending}>
            {topUp.isPending ? 'Создаем платеж...' : 'Продолжить'}
          </button>
        </form>
      ) : null}

      {showRefund ? (
        <form className={classes.card} onSubmit={handleRefund}>
          <h2 className={classes.cardTitle}>Заявка на возврат</h2>
          <p className={classes.muted}>
            Возврат выполняется вручную после проверки. Сумма резервируется на балансе до решения.
          </p>
          <WalletRubAmountInput
            value={refundAmount}
            onChange={setRefundAmount}
            disabled={refundCreate.isPending}
          />
          <label className={classes.field}>
            <span className={classes.label}>Комментарий</span>
            <textarea
              className={classes.area}
              rows={3}
              value={refundReason}
              onChange={(event) => setRefundReason(event.target.value)}
            />
          </label>
          {formError ? <p className={classes.error}>{formError}</p> : null}
          <button type='submit' className={classes.primary} disabled={refundCreate.isPending}>
            {refundCreate.isPending ? 'Отправляем...' : 'Отправить заявку'}
          </button>
        </form>
      ) : null}

      {refundRequests && refundRequests.length > 0 ? (
        <section className={classes.card}>
          <h2 className={classes.cardTitle}>Заявки на возврат</h2>
          <ul className={classes.list}>
            {refundRequests.map((item) => (
              <li key={item.id} className={classes.line}>
                <strong>
                  {formatRubFull(item.amount_rub)} - {item.status_label}
                </strong>
                <span>{formatDate(item.created_at)}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <WalletTransactionHistory />

      <p className={classes.small}>
        Кошелек относится к операциям сервиса. Стоимость занятий согласуют ученик и репетитор.{' '}
        <Link to='/teacher/questions'>Подробнее</Link>
      </p>
    </main>
  );
};
